import { useReducer, useCallback, useRef, useEffect } from 'react';
import { unique } from '@agilepoint/utils-array';
import { IChangeArgs, IFileInfo, ReadMode } from './file-upload.types';
import { generateId } from '@agilepoint/utils-common';
import { IFileUploadProps } from '.';

export function getFileInfo(file: File, readMode: ReadMode): Promise<IFileInfo> {
    return new Promise((resolve, reject) => {
        const fileReader = new FileReader();
        fileReader.addEventListener('load', () => {
            resolve({
                id: generateId(),
                size: file.size,
                content: fileReader.result,
                name: file.name,
                type: file.type
            });
        });
        fileReader.addEventListener('error', () => reject({
            error: 'Failed to read content.',
            name: file.name
        }));
        fileReader[readMode](file);
    });
}

export function getAllFilesInfo(fileList: File[], readMode: ReadMode) {
    return Promise.allSettled(fileList.map((file) => getFileInfo(file, readMode)));
}

type UpsertFiles = {
    type: 'UPSERT',
    payload: IFileInfo[]
}

type RemoveFile = {
    type: 'REMOVE',
    payload: string
}

type FileRepositoryAction = (UpsertFiles | RemoveFile) & { multiple?: boolean };

type ChangeInfo = Pick<IChangeArgs, 'action' | 'changedFileIds'> | null;

const stateReducer = (state: IFileInfo[], action: FileRepositoryAction) => {
    switch (action.type) {
        case 'UPSERT': {
            if (action.multiple) {
                return unique(action.payload.concat(state), i => `${i.name}_${i.type}`);
            }
            const [firstFile] = action.payload;
            return firstFile ? [firstFile] : [];
        }
        case 'REMOVE':
            return action.multiple ? state.filter(i => i.id !== action.payload) : [];

        default:
            return state;
    }
};

const getFileId = (file: IFileInfo) => file.id;

export function useFileRepository(initialFiles: IFileInfo[] = [], multiple?: boolean, onChange?: IFileUploadProps['onChange']) {
    const [selectedFiles, dispatch] = useReducer(stateReducer, initialFiles);
    const changeInfoRef = useRef<ChangeInfo>(null);

    useEffect(() => {
        if (onChange === undefined || !changeInfoRef.current) {
            return;
        }
        const changeInfo = changeInfoRef.current;
        onChange({ ...changeInfo, selectedFiles });
        changeInfoRef.current = null;
    }, [onChange, selectedFiles]);

    const upsert = useCallback((files: IFileInfo[]) => {
        dispatch({
            type: 'UPSERT',
            payload: files,
            multiple
        });
        changeInfoRef.current = {
            action: 'add',
            changedFileIds: files.map(getFileId)
        };
    }, [multiple]);

    const remove = useCallback((fileId: string) => {
        dispatch({
            type: 'REMOVE',
            payload: fileId,
            multiple
        });
        changeInfoRef.current = {
            action: 'remove',
            changedFileIds: [fileId]
        };
    }, [multiple]);

    return { selectedFiles, upsert, remove };
}
